import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Container, Dialog, DialogContent, Grid, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import productsData from '../data/products.json';

const CoreModules = () => {
    const { t } = useTranslation();
    const { mode } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const productId = searchParams.get('product') || '5'; // Default to Smart Organization
    const [openMarketingModal, setOpenMarketingModal] = useState(false);
    const [openQuarterlyModal, setOpenQuarterlyModal] = useState(false);
    const [openFinancialModal, setOpenFinancialModal] = useState(false);

    // Load product content dynamically from products.json
    const getProductContent = () => {
        const product = productsData.products.find(p => p.id.toString() === productId);
        if (product) {
            return {
                title: `${t(`products.product${product.id}.name`) || product.name} Modules`,
                description: t(`products.product${product.id}.desc`) || product.description,
                name: t(`products.product${product.id}.name`) || product.name,
            };
        }
        // Default fallback
        return {
            title: t('smartOrg.coreModules.title'),
            description: 'Complete organizational management modules',
            name: 'Smart Organization',
        };
    };

    const productContent = getProductContent();

    const modules = [
        { id: 1, name: t('smartOrg.coreModules.modules.orgchat'), position: 'top' },
        { id: 2, name: t('smartOrg.coreModules.modules.planner'), position: 'bottom' },
        { id: 3, name: t('smartOrg.coreModules.modules.calendar'), position: 'top' },
        { id: 4, name: t('smartOrg.coreModules.modules.automation'), position: 'bottom' },
        { id: 5, name: t('smartOrg.coreModules.modules.assistant'), position: 'top' },
        { id: 6, name: t('smartOrg.coreModules.modules.meeting'), position: 'bottom' },
        { id: 7, name: t('smartOrg.coreModules.modules.textra'), position: 'top' },
    ];

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: mode === 'dark'
                    ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                py: { xs: 10, md: 12 },
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="xl">
                {/* Header */}
                <Box sx={{ mb: 8 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            fontWeight: 'bold',
                            color: mode === 'dark' ? 'white' : '#1e293b',
                            mb: 2,
                        }}
                    >
                        {productContent.title}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontSize: { xs: '1.125rem', md: '1.25rem' },
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                        }}
                    >
                        {productContent.description}
                    </Typography>
                </Box>

                {/* Timeline Container */}
                <Box sx={{ position: 'relative', mt: 10 }}>
                    {/* Horizontal Timeline Line */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: 3,
                            background: 'linear-gradient(90deg, #f97316 0%, #ef4444 100%)',
                            transform: 'translateY(-50%)',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                right: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 0,
                                height: 0,
                                borderTop: '8px solid transparent',
                                borderBottom: '8px solid transparent',
                                borderLeft: '12px solid #ef4444',
                            },
                        }}
                    />

                    {/* Modules Grid */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: 2,
                            position: 'relative',
                        }}
                    >
                        {modules.map((module, index) => (
                            <Box
                                key={module.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    position: 'relative',
                                }}
                            >
                                {/* Module Card */}
                                <Box
                                    sx={{
                                        order: module.position === 'top' ? 1 : 3,
                                        mb: module.position === 'top' ? 8 : 0,
                                        mt: module.position === 'bottom' ? 8 : 0,
                                    }}
                                >
                                    <Box
                                        onClick={() => {
                                            if (module.id === 1) {
                                                navigate('/detailed-product?product=5');
                                            } else if (module.id === 2) {
                                                navigate('/detailed-product?product=5');
                                            } else if (module.id === 3) {
                                                navigate('/detailed-product?product=5');
                                            } else if (module.id === 4) {
                                                navigate('/detailed-product?product=5');
                                            } else if (module.id === 5) {
                                                navigate('/detailed-product?product=5');
                                            } else if (module.id === 6) {
                                                navigate('/detailed-product?product=3');
                                            } else if (module.id === 7) {  //textra
                                                navigate('/detailed-product?product=4');
                                            }
                                        }}
                                        sx={{
                                            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.95)' : '#1e293b',
                                            borderRadius: 3,
                                            p: 3,
                                            minWidth: 180,
                                            textAlign: 'center',
                                            border: mode === 'dark' ? '3px solid #1e293b' : '3px solid #ffffff',
                                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: '0 12px 32px rgba(249, 115, 22, 0.4)',
                                                borderColor: '#f97316',
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                color: mode === 'dark' ? '#1e293b' : '#ffffff',
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {module.name}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Vertical Connecting Line */}
                                <Box
                                    sx={{
                                        order: 2,
                                        height: 80,
                                        width: 2,
                                        background: '#f97316',
                                        position: 'relative',
                                    }}
                                />

                                {/* Timeline Number */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        background: '#f97316',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: mode === 'dark' ? '3px solid #0f172a' : '3px solid #ffffff',
                                        zIndex: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '1.25rem',
                                        }}
                                    >
                                        {module.id}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Hexagons Decoration */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 50,
                        right: 100,
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                            background: mode === 'dark' ? 'rgba(147, 197, 253, 0.3)' : 'rgba(30, 41, 59, 0.2)',
                        }}
                    />
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                            background: '#f97316',
                        }}
                    />
                </Box>

                {/* Small Hexagon */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 180,
                        right: 200,
                        width: 40,
                        height: 40,
                        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                        background: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                    }}
                />

                {/* Action Buttons */}
                <Box sx={{ mt: 8, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Button
                        onClick={() => setOpenMarketingModal(true)}
                        variant="contained"
                        startIcon={<CampaignIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                            color: 'white',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            px: 6,
                            py: 2.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            boxShadow: '0 8px 24px rgba(249, 115, 22, 0.3)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 32px rgba(249, 115, 22, 0.4)',
                            },
                        }}
                    >
                        {t('smartOrg.coreModules.marketing.button')}
                    </Button>

                    <Button
                        onClick={() => setOpenQuarterlyModal(true)}
                        variant="contained"
                        startIcon={<CampaignIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                            color: 'white',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            px: 6,
                            py: 2.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            boxShadow: '0 8px 24px rgba(249, 115, 22, 0.3)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 32px rgba(249, 115, 22, 0.4)',
                            },
                        }}
                    >
                        {t('smartOrg.coreModules.quarterly.button')}
                    </Button>

                    <Button
                        onClick={() => setOpenFinancialModal(true)}
                        variant="contained"
                        startIcon={<CampaignIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                            color: 'white',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            px: 6,
                            py: 2.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            boxShadow: '0 8px 24px rgba(249, 115, 22, 0.3)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 32px rgba(249, 115, 22, 0.4)',
                            },
                        }}
                    >
                        {t('smartOrg.coreModules.financial.button')}
                    </Button>
                </Box>

                {/* Future Initiatives Section */}
                <Box sx={{ mt: 12, mb: 8 }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontSize: { xs: '2rem', md: '3rem' },
                            fontWeight: 'bold',
                            color: mode === 'dark' ? 'white' : '#1e293b',
                            mb: 6,
                            textAlign: { xs: 'center', md: 'left' },
                        }}
                    >
                        {t('smartOrg.coreModules.future.title')}
                    </Typography>

                    <Grid container spacing={4} alignItems="flex-start">
                        {/* Left Side - Initiatives List */}
                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {[
                                    { number: '1', key: 'item1' },
                                    { number: '2', key: 'item2' },
                                    { number: '3', key: 'item3' },
                                ].map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            gap: 3,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '1.5rem', md: '2rem' },
                                                fontWeight: 'bold',
                                                color: mode === 'dark' ? 'white' : '#1e293b',
                                                minWidth: { xs: 30, md: 40 },
                                            }}
                                        >
                                            {item.number}.
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '1rem', md: '1.125rem' },
                                                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                                lineHeight: 1.8,
                                            }}
                                        >
                                            {t(`smartOrg.coreModules.future.${item.key}`)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        {/* Right Side - Environmental Items */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                {[
                                    { icon: '🌱', key: 'env1' },
                                    { icon: '🌍', key: 'env2' },
                                    { icon: '♻️', key: 'env3' },
                                    { icon: '💧', key: 'env4' },
                                ].map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                fontSize: '1.5rem',
                                                minWidth: 40,
                                            }}
                                        >
                                            {item.icon}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: '1rem', md: '1.125rem' },
                                                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {t(`smartOrg.coreModules.future.${item.key}`)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>

            {/* Marketing Strategies Modal */}
            <Dialog
                open={openMarketingModal}
                onClose={() => setOpenMarketingModal(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        background: mode === 'dark' ? '#1e3a5f' : '#ffffff',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                <DialogContent
                    sx={{ p: 0, position: 'relative' }}
                >
                    <IconButton
                        onClick={() => setOpenMarketingModal(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            zIndex: 10,
                            '&:hover': {
                                background: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Box sx={{
                        background: mode === 'dark' ? '#1e3a5f' : '#1e3a5f',
                        p: { xs: 4, md: 6 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: { xs: '2rem', md: '3rem' },
                                fontWeight: 'bold',
                                color: 'white',
                                mb: 4,
                                lineHeight: 1.2,
                            }}
                        >
                            {t('smartOrg.coreModules.marketing.title')}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {[
                                { key: 'item1', emoji: '🎯' },
                                { key: 'item2', emoji: '👥' },
                                { key: 'item3', emoji: '🤝' },
                            ].map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontSize: '1.5rem',
                                            minWidth: 40,
                                        }}
                                    >
                                        {item.emoji}
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            fontSize: { xs: '1rem', md: '1.125rem' },
                                            lineHeight: 1.8,
                                        }}
                                    >
                                        {t(`smartOrg.coreModules.marketing.${item.key}`)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Quarterly Targets Modal */}
            <Dialog
                open={openQuarterlyModal}
                onClose={() => setOpenQuarterlyModal(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        background: mode === 'dark' ? '#1e3a5f' : '#ffffff',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                <DialogContent
                    sx={{ p: 0, position: 'relative' }}
                >
                    <IconButton
                        onClick={() => setOpenQuarterlyModal(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            zIndex: 10,
                            '&:hover': {
                                background: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Grid container sx={{ minHeight: '500px' }}>
                        {/* Left Side - Content */}
                        <Grid item xs={12} md={5} sx={{
                            background: mode === 'dark' ? '#1e3a5f' : '#1e3a5f',
                            p: { xs: 4, md: 6 },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                        }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: '2rem', md: '3rem' },
                                    fontWeight: 'bold',
                                    color: 'white',
                                    mb: 4,
                                    lineHeight: 1.2,
                                }}
                            >
                                {t('smartOrg.coreModules.quarterly.title')}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                {[
                                    { key: 'item1', emoji: '📈' },
                                    { key: 'item2', emoji: '💡' },
                                    { key: 'item3', emoji: '👥' },
                                    { key: 'item4', emoji: '⚙️' },
                                ].map((item, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                fontSize: '1.5rem',
                                                minWidth: 40,
                                            }}
                                        >
                                            {item.emoji}
                                        </Box>
                                        <Typography
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.9)',
                                                fontSize: { xs: '1rem', md: '1.125rem' },
                                                lineHeight: 1.8,
                                            }}
                                        >
                                            {t(`smartOrg.coreModules.quarterly.${item.key}`)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        {/* Right Side - Table */}
                        <Grid item xs={12} md={7} sx={{
                            background: mode === 'dark' ? '#0f172a' : '#f8f9fa',
                            p: { xs: 3, md: 5 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Box sx={{ width: '100%', maxWidth: 700 }}>
                                {/* Table */}
                                <Box
                                    sx={{
                                        border: '2px solid #f97316',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: '0 8px 24px rgba(249, 115, 22, 0.2)',
                                    }}
                                >
                                    {/* Table Header */}
                                    <Grid container sx={{ background: '#f97316' }}>
                                        <Grid item xs={3}>
                                            <Box sx={{ p: 2, borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                    {t('smartOrg.coreModules.quarterly.table.quarter')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Box sx={{ p: 2, borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                    {t('smartOrg.coreModules.quarterly.table.revenue')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Box sx={{ p: 2, borderRight: '1px solid rgba(255, 255, 255, 0.2)' }}>
                                                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                    {t('smartOrg.coreModules.quarterly.table.market')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Box sx={{ p: 2 }}>
                                                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                                                    {t('smartOrg.coreModules.quarterly.table.customer')}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {/* Table Rows */}
                                    {[
                                        { quarter: 'Q1', revenue: '12', market: '2', customer: '500' },
                                        { quarter: 'Q2', revenue: '15', market: '3', customer: '600' },
                                        { quarter: 'Q3', revenue: '18', market: '4', customer: '700' },
                                        { quarter: 'Q4', revenue: '20', market: '5', customer: '800' },
                                    ].map((row, index) => (
                                        <Grid
                                            container
                                            key={index}
                                            sx={{
                                                background: index % 2 === 0
                                                    ? (mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff')
                                                    : (mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f9f9f9'),
                                                borderTop: '1px solid rgba(249, 115, 22, 0.2)',
                                            }}
                                        >
                                            <Grid item xs={3}>
                                                <Box sx={{ p: 2, borderRight: '1px solid rgba(249, 115, 22, 0.1)' }}>
                                                    <Typography sx={{
                                                        color: mode === 'dark' ? '#ffffff' : '#1e293b',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.875rem', md: '1rem' }
                                                    }}>
                                                        {row.quarter}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ p: 2, borderRight: '1px solid rgba(249, 115, 22, 0.1)' }}>
                                                    <Typography sx={{
                                                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                                        fontSize: { xs: '0.875rem', md: '1rem' }
                                                    }}>
                                                        {row.revenue}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ p: 2, borderRight: '1px solid rgba(249, 115, 22, 0.1)' }}>
                                                    <Typography sx={{
                                                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                                        fontSize: { xs: '0.875rem', md: '1rem' }
                                                    }}>
                                                        {row.market}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Box sx={{ p: 2 }}>
                                                    <Typography sx={{
                                                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                                        fontSize: { xs: '0.875rem', md: '1rem' }
                                                    }}>
                                                        {row.customer}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* Financial Snapshot Modal */}
            <Dialog
                open={openFinancialModal}
                onClose={() => setOpenFinancialModal(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        background: mode === 'dark' ? '#1e3a5f' : '#ffffff',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                <DialogContent
                    sx={{ p: { xs: 3, md: 5 }, position: 'relative' }}
                >
                    <IconButton
                        onClick={() => setOpenFinancialModal(false)}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                            background: mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            zIndex: 10,
                            '&:hover': {
                                background: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Title */}
                    <Typography
                        variant="h3"
                        sx={{
                            fontSize: { xs: '2rem', md: '3rem' },
                            fontWeight: 'bold',
                            color: mode === 'dark' ? 'white' : '#1e293b',
                            mb: 5,
                            textAlign: 'left',
                        }}
                    >
                        {t('smartOrg.coreModules.financial.title')}
                    </Typography>

                    {/* Financial Table */}
                    <Box
                        sx={{
                            border: '3px solid #f97316',
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 8px 24px rgba(249, 115, 22, 0.3)',
                        }}
                    >
                        {/* Table Header */}
                        <Grid container sx={{ background: '#f97316' }}>
                            <Grid item xs={3}>
                                <Box sx={{ p: 2.5, borderRight: '1px solid rgba(255, 255, 255, 0.3)' }}>
                                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1.125rem' } }}>
                                        {t('smartOrg.coreModules.financial.table.metric')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box sx={{ p: 2.5, borderRight: '1px solid rgba(255, 255, 255, 0.3)' }}>
                                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1.125rem' } }}>
                                        {t('smartOrg.coreModules.financial.table.current')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box sx={{ p: 2.5, borderRight: '1px solid rgba(255, 255, 255, 0.3)' }}>
                                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1.125rem' } }}>
                                        {t('smartOrg.coreModules.financial.table.previous')}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={3}>
                                <Box sx={{ p: 2.5 }}>
                                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '0.875rem', md: '1.125rem' } }}>
                                        {t('smartOrg.coreModules.financial.table.change')}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Table Rows */}
                        {[
                            { metric: t('smartOrg.coreModules.financial.rows.revenue'), current: '$2,500,000', previous: '$2,200,000', change: '+14%', positive: true },
                            { metric: t('smartOrg.coreModules.financial.rows.expenses'), current: '$1,200,000', previous: '$1,400,000', change: '-14%', positive: true },
                            { metric: t('smartOrg.coreModules.financial.rows.profit'), current: '$1,000,000', previous: '$800,000', change: '+25%', positive: true },
                            { metric: t('smartOrg.coreModules.financial.rows.margin'), current: '40%', previous: '36%', change: '+4%', positive: true },
                            { metric: t('smartOrg.coreModules.financial.rows.reserves'), current: '$5,000,000', previous: '$4,500,000', change: '+11%', positive: true },
                        ].map((row, index) => (
                            <Grid
                                container
                                key={index}
                                sx={{
                                    background: index % 2 === 0
                                        ? (mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f5f5f5')
                                        : (mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#ffffff'),
                                    borderTop: '1px solid rgba(249, 115, 22, 0.2)',
                                }}
                            >
                                <Grid item xs={3}>
                                    <Box sx={{ p: 2.5, borderRight: '1px solid rgba(249, 115, 22, 0.1)' }}>
                                        <Typography sx={{
                                            color: mode === 'dark' ? '#ffffff' : '#1e293b',
                                            fontWeight: 600,
                                            fontSize: { xs: '0.875rem', md: '1rem' }
                                        }}>
                                            {row.metric}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={3}>
                                    <Box sx={{ p: 2.5, borderRight: '1px solid rgba(249, 115, 22, 0.1)' }}>
                                        <Typography sx={{
                                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                            fontSize: { xs: '0.875rem', md: '1rem' }
                                        }}>
                                            {row.current}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={3}>
                                    <Box sx={{ p: 2.5, borderRight: '1px solid rgba(249, 115, 22, 0.1)' }}>
                                        <Typography sx={{
                                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#334155',
                                            fontSize: { xs: '0.875rem', md: '1rem' }
                                        }}>
                                            {row.previous}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={3}>
                                    <Box sx={{ p: 2.5 }}>
                                        <Typography sx={{
                                            color: row.positive ? '#4caf50' : '#f44336',
                                            fontWeight: 600,
                                            fontSize: { xs: '0.875rem', md: '1rem' }
                                        }}>
                                            {row.change}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default CoreModules;

